import { Box } from "@mui/material";
import Flex from "@/components/Flex";
import StyledDialog from "@/components/StyledDialog";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import { REMOVE_PATIENT_SESSIONS_LABEL } from "../group-visit-constant";

export type RemovePatientSessionsModalProps = {
  open: boolean;
  onClose: () => void;
  /** Wording switches to the plural for a bulk removal. */
  isBulk?: boolean;
  onConfirm: (cancelUpcomingSessions: boolean) => void;
  isSubmitting?: boolean;
};

/**
 * Removing patients from a group — keep or cancel their already scheduled
 * sessions, or back out.
 *
 * @author Rajkumar Rathod
 */
const RemovePatientSessionsModal = ({
  open,
  onClose,
  isBulk,
  onConfirm,
  isSubmitting,
}: RemovePatientSessionsModalProps) => (
  <StyledDialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="sm"
    dialogTitle={
      <Text
        {...CHART_TYPOGRAPHY.body3}
        fontWeight={CHART_TYPOGRAPHY.weight.semiBold}
        c={CHART_COLORS.textDarker}
      >
        {isBulk
          ? REMOVE_PATIENT_SESSIONS_LABEL.bulkTitle
          : REMOVE_PATIENT_SESSIONS_LABEL.title}
      </Text>
    }
    dialogAction={
      <>
        <StyledButtonV2
          label={REMOVE_PATIENT_SESSIONS_LABEL.back}
          variant="outlined"
          disabled={isSubmitting}
          onClick={onClose}
        />
        <StyledButtonV2
          label={REMOVE_PATIENT_SESSIONS_LABEL.keepSessions}
          variant="outlined"
          disabled={isSubmitting}
          onClick={() => onConfirm(false)}
        />
        <StyledButtonV2
          label={REMOVE_PATIENT_SESSIONS_LABEL.cancelSessions}
          variant="primary"
          disabled={isSubmitting}
          onClick={() => onConfirm(true)}
        />
      </>
    }
  >
    <Flex flexDirection="column" gap={1.5}>
      <Text {...CHART_TYPOGRAPHY.body4} c={CHART_COLORS.textDarker}>
        {isBulk
          ? REMOVE_PATIENT_SESSIONS_LABEL.bulkContent
          : REMOVE_PATIENT_SESSIONS_LABEL.content}
      </Text>

      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderRadius: CHART_DIMENSIONS.borderRadius,
          backgroundColor: CHART_COLORS.badgeWarningBg,
        }}
      >
        <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.badgeWarningText}>
          {REMOVE_PATIENT_SESSIONS_LABEL.pastSessionsNote}
        </Text>
      </Box>
    </Flex>
  </StyledDialog>
);

export default RemovePatientSessionsModal;
